import { GRID_CHECKBOX_SELECTION_COL_DEF, GridApiCommon } from "@mui/x-data-grid";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-premium";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { DatePicker } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { MutableRefObject, memo, useCallback } from "react";

import CommonTableToolbar from "@/components/CommonTableToolbar";
import { LocaleStorageKeys } from "@/constants";
import DriverFilter from "@/pages/Plannn/components/table/PlanDriverFilter";
import {
    useTrackingActions,
    useTrackingDate,
    useTrackingFilters,
    useTrackingIsFullScreen,
} from "@/stores/use-tracking-table";
import { IPlanPassenger, Task } from "@/types/tasks.types";

const columnNames = [
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field,
    GRID_CHECKBOX_SELECTION_COL_DEF.field,
    "index",
    "status",
    "pickUp",
    "origin",
    "destination",
    "passengerCount",
    "driverId",
    "actions",
];

const defaultTrackingGridOptions: GridInitialStatePremium = {
    sorting: {
        sortModel: [{ field: "pickUp", sort: "asc" }],
    },
    pinnedColumns: {
        left: [GRID_DETAIL_PANEL_TOGGLE_COL_DEF.field, GRID_CHECKBOX_SELECTION_COL_DEF.field, "index", "status"],
        right: ["actions"],
    },
    columns: {
        orderedFields: columnNames,
        columnVisibilityModel: columnNames.reduce(
            (acc, field) => {
                acc[field] = true;
                return acc;
            },
            {} as Record<string, boolean>
        ),
    },
};

const TrackingToolbar = memo(function TrackingToolbar({
    reloadTable,
    apiRef,
    rows,
}: {
    reloadTable: () => void;
    apiRef: MutableRefObject<GridApiCommon>;
    rows: Task<IPlanPassenger<"api">>[];
}) {
    const { updateFullScreen, setDate, updateFilters } = useTrackingActions();
    const isFullScreen = useTrackingIsFullScreen();
    const date = useTrackingDate();
    const filters = useTrackingFilters();

    const handleDriverChange = useCallback(
        (driverIds: string[]) => {
            updateFilters({
                ...filters,
                selectedDrivers: driverIds,
            });
        },
        [updateFilters, filters]
    );
    const getDriverId = useCallback((row: Task<IPlanPassenger<"api">>) => row.driverId || "", []);
    return (
        <CommonTableToolbar
            apiRef={apiRef}
            isFullScreen={isFullScreen}
            onFullScreenToggle={updateFullScreen}
            onRefresh={reloadTable}
            storageKey={LocaleStorageKeys.TRACKING_GRID_SETTINGS}
            defaultOptions={defaultTrackingGridOptions}
        >
            <DatePicker
                className="!w-48"
                value={date}
                onChange={(date: DateTime | null) => {
                    if (date) setDate(date);
                }}
            />
            <DriverFilter<Task<IPlanPassenger<"api">>>
                allRows={rows}
                currentDrivers={filters.selectedDrivers}
                onDriverChange={handleDriverChange}
                getDriverId={getDriverId}
                driverKey="id"
            />
        </CommonTableToolbar>
    );
});

export default TrackingToolbar;
